import { Capacitor } from "@capacitor/core";
import { Preferences } from "@capacitor/preferences";
import { useContext, useState } from "react";
import { Link, LoaderFunction, useLoaderData, useLocation, useNavigate } from "react-router-dom";

import { Loader } from "@/atoms";
import { useTranslation } from "@/core/i18n";
import { getMultiPlatformList } from "api/methods";
import { Models } from "api/types";
import { Icon } from "atoms/icon/Icon";
import ChevronLeft from "atoms/icon/icons/chevron_left.svg";
import PlusIcon from "atoms/icon/icons/ic_plus_small_ol.svg";
import { IconButton } from "atoms/icon-button/IconButton";
import { RepeatMDFooter } from "atoms/repeatmd-footer/RepeatMDFooter";
import { TextButton } from "atoms/text-button/TextButton";
import { useAppState } from "contexts/AppContext";
import { PlatformLocationsList } from "organisms/platform-locations-list/PlatformLocationsList";
import { queryClient, ThemeContext } from "pages/Root";
import Services from "services/Services";
import { currencyService } from "toolbox/CurrencyService";
import { waitObj } from "toolbox/Promise";
import { changeDayjsLocale } from "utils/dayjsUtils";
import { isNativeMobileApp } from "utils/platform.helpers";

const load = async () => {
  let platformDomainTokens: Models<"GetMultiPlatformUserPlatformResult">[] = [];
  try {
    platformDomainTokens = await queryClient
      .fetchQuery({ ...getMultiPlatformList.keyed() })
      .then((res) => res.platformDomainTokens);
  } catch {
    if (isNativeMobileApp()) {
      const { value } = await Preferences.get({
        key: "platformDomainTokens",
      });
      const tokens = value ? JSON.parse(value) : [];
      platformDomainTokens = tokens as Models<"GetMultiPlatformUserPlatformResult">[];
    }
  }

  return waitObj({
    platformDomainTokens,
  });
};

type LoaderData = Awaited<ReturnType<typeof load>>;
export const changePlatformLoader: LoaderFunction = () => {
  return load();
};

export const getChangePlatformDestination = (search: string) =>
  new URLSearchParams(search).get("fromSignIn") === "true" ? "/home?fromSignIn=true" : "/home";

export const ChangePlatformPage = () => {
  const { t } = useTranslation();
  const { platformDomainTokens } = useLoaderData() as LoaderData;

  const navigate = useNavigate();
  const routeLocation = useLocation();
  const appState = useAppState();
  const { resetTheme } = useContext(ThemeContext);
  const [isLoading, setIsLoading] = useState(false);

  const handlePlatformClick = async (value: Models<"GetMultiPlatformUserPlatformResult">) => {
    setIsLoading(true);
    const domainScheme = new URL(import.meta.env.REACT_APP_API_BASE_URL ?? location.href).protocol;
    const apiUrl = `${domainScheme}//${value.platformDomain}`;

    // don't do anything if the user is already on the selected platform
    if (appState.apiUrl !== apiUrl) {
      appState.setApiUrl(apiUrl);
      appState.setAppIcons(
        platformDomainTokens.map((platform) => `launcher_${platform.platformSubdomain}`),
      );
      appState.setPlatformId(value.platformId);
      await Services.auth.setToken(value.token);
      if (value.platformStripeRegion) {
        changeDayjsLocale(value.platformStripeRegion);
        currencyService.setCurrency(value.platformStripeRegion);
      }
      resetTheme();
    }

    queryClient.clear();
    navigate(getChangePlatformDestination(routeLocation.search));
  };

  return (
    <div className="flex h-screen flex-col bg-one">
      <Loader fullScreen visible={isLoading} />
      <div className="bg-white pl-3 pt-[calc(20px_+_env(safe-area-inset-top))] shadow-[0px_2px_4px_rgba(0,0,0,0.05)]">
        <span className="flex items-center justify-between">
          <Link to="..">
            <IconButton size="small" style="iconOnly" svg={ChevronLeft} />
          </Link>
          <div className="text-sub2 uppercase">{t("changePlatformPage.selectYourLocation")}</div>
          <div className="invisible">
            <IconButton size="small" style="iconOnly" svg={ChevronLeft} />
          </div>
        </span>
      </div>
      <div className="flex flex-1 flex-col justify-between overflow-auto px-4 pb-[calc(env(safe-area-inset-bottom))] pt-4">
        <div>
          <PlatformLocationsList
            platforms={platformDomainTokens}
            onItemClick={handlePlatformClick}
          />
          {!appState.platformId && (
            <div className="flex justify-center pt-5">
              <Link to="/signin/platform-search">
                <TextButton>
                  <Icon size="small" svg={PlusIcon} color="brandColor" />
                  {t("changePlatformPage.addNewLocation")}
                </TextButton>
              </Link>
            </div>
          )}
        </div>
        <RepeatMDFooter />
      </div>
    </div>
  );
};
